import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";
import {
  PromotionCampaignStatusEnum,
  promotionCampaignStatusLabels,
} from "../status";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./promotion-campaign-form-constants";

export const PromotionCampaignFormDialog = ({
  defaultValues,
  onSubmit,
  isOpen,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              <CustomInputField
                required
                name="name"
                label="Nama"
                register={register}
                helperText={errors.name?.message}
                error={Boolean(errors.name)}
              />
              <CustomInputField
                name="description"
                label="Deskripsi"
                register={register}
                helperText={errors.description?.message}
                error={Boolean(errors.description)}
                multiline
                minRows={3}
              />
              <Controller
                control={control}
                name="status"
                render={({ field, fieldState }) => (
                  <CustomSelect
                    required
                    value={field.value}
                    onChange={field.onChange}
                    id="status"
                    label="Status"
                    options={[
                      PromotionCampaignStatusEnum.DRAFT,
                      PromotionCampaignStatusEnum.ACTIVE,
                      PromotionCampaignStatusEnum.PAUSED,
                    ].map((value) => ({
                      value: value,
                      label: promotionCampaignStatusLabels[value],
                    }))}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

PromotionCampaignFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
